/* eslint-disable sonarjs/cognitive-complexity */
import React, {type FC, ReactNode, useCallback, useState} from 'react';
import {IProduct} from '../../../../types/galleryTypes';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import s from './ProductOptions.scss';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {Experiments} from '../../../../constants';
import {RevealAnimation} from '../../RevealAnimation/RevealAnimation';
import {ProductColorOption} from './ProductColorOption/ProductColorOption';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {getAllVisibleOptions, getAllVisibleColorOptions, getAllVisibleTextualOptions} from './utils/getVisibleOptions';
import {ProductTextualOption} from './ProductTextualOptions/ProductTextualOption';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {useOptionFocusManagement} from './utils/hooks/useOptionFocusManagement';

type IProductOption = IProduct['options'][0];
export interface ProductOptionsChangeData {
  optionType: ProductOptionType;
}

export interface IProductOptionsProps extends IGalleryGlobalProps {
  product: IProduct;
  selectionIds: number[];
  isLoading?: boolean;
  readOnly?: boolean;

  onSelectionIdsChange(
    optionSelections: IProductOptionsProps['selectionIds'],
    changeData: ProductOptionsChangeData
  ): Promise<void>;

  variantsAvailability: IProductSelectionAvailabilityMap;
  isItemHovered: boolean;
}

export interface OptionRenderParams {
  option: IProduct['options'][number];
  availability: IProductSelectionAvailabilityMap[string];
  url?: string;
}

export enum DataHook {
  ProductOption = 'product-option',
  DropdownOption = 'product-options-dropdown',
  ColorOption = 'product-options-color',
  ColorOptionMore = 'product-options-color-more',
  OptionWrapper = 'product-option-wrapper',
  RevealAnimationWrapper = 'product-option-reveal-animation-wrapper',
  ButtonOption = 'product-option-button',
  ButtonOptionMore = 'product-options-button-more',
  ProductOptionLoader = 'product-option-loader',
}

const ProductOptionsComponent: FC<IProductOptionsProps> = ({
  product,
  selectionIds,
  isLoading,
  onSelectionIdsChange,
  variantsAvailability,
  isItemHovered,
  readOnly,
}) => {
  const {productsManifest, isOptionsRevealEnabled} = useGlobals();
  const {experiments} = useExperiments();
  const isQualityOptionsStylingChangesEnabled = experiments.enabled(Experiments.EnableQualityOptionsStylingChanges);
  const shouldShowFirstProductOptionInGallery = experiments.enabled(Experiments.ShouldShowFirstProductOptionInGallery);
  const [latestActiveOptionId, setLatestActiveOptionId] = useState<string>('');

  const isOptionSelected = useCallback(
    (option: IProductOption) => option.selections.find(({id}) => selectionIds.includes(id)) !== undefined,
    [selectionIds]
  );

  const isFirst = useCallback(
    (option: IProductOption) => {
      const options = getAllVisibleOptions(product.options);
      return options.length && options[0].id === option.id;
    },
    [product.options]
  );

  const isVisible = useCallback(
    (option: IProductOption) => {
      const options = getAllVisibleOptions(product.options);
      const index = options.findIndex(({id}) => option.id === id);
      return (
        isOptionSelected(option) ||
        (isFirst(option) && isItemHovered) ||
        (options[index] && isOptionSelected(options[index]))
      );
    },
    [isOptionSelected, isFirst, isItemHovered, product.options]
  );

  const {registerOptionRef} = useOptionFocusManagement({product, isOptionSelected});

  const shouldRenderLoader = useCallback(
    (optionId: string) => {
      return isLoading && latestActiveOptionId !== optionId;
    },
    [isLoading, latestActiveOptionId]
  );

  const handleOptionChange = useCallback(
    async (selectedOptionIds: number[], changeData: ProductOptionsChangeData, optionId: string) => {
      setLatestActiveOptionId(optionId);
      return onSelectionIdsChange(selectedOptionIds, changeData);
    },
    [onSelectionIdsChange]
  );

  const renderColorOption = useCallback(
    (option: IProductOption) => {
      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.color]: isQualityOptionsStylingChangesEnabled,
            [s.oldColor]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductColorOption
            url={productsManifest[product.id].url}
            option={option}
            availability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              handleOptionChange(selectedOptionIds, changeData, option.id)
            }
            isLoading={shouldRenderLoader(option.id)}
          />
        </div>
      );
    },
    [
      handleOptionChange,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      selectionIds,
      shouldRenderLoader,
      variantsAvailability,
      product,
      registerOptionRef,
    ]
  );

  const getColorOptions = useCallback(() => {
    const colorOptions = getAllVisibleColorOptions(product.options);
    return colorOptions.map((option) => renderColorOption(option));
  }, [renderColorOption, product.options]);

  const wrapAnimation = useCallback(
    (child: ReactNode, isOptionVisible: boolean, preserveSpace: boolean) => {
      return isOptionsRevealEnabled ? (
        <RevealAnimation
          isVisible={isOptionVisible}
          preserveSpace={preserveSpace}
          data-hook={DataHook.RevealAnimationWrapper}>
          {child}
        </RevealAnimation>
      ) : (
        child
      );
    },
    [isOptionsRevealEnabled]
  );

  const renderTextualOption = useCallback(
    (option: IProductOption) => {
      return (
        <div
          key={`${option.id}${isOptionsRevealEnabled ? isVisible(option) : ''}`}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.dropdown]: isQualityOptionsStylingChangesEnabled,
            [s.oldDropdown]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductTextualOption
            productUrl={productsManifest[product.id].url}
            option={option}
            availability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            readOnly={readOnly}
            isLoading={shouldRenderLoader(option.id)}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              handleOptionChange(selectedOptionIds, changeData, option.id)
            }
          />
        </div>
      );
    },
    [
      handleOptionChange,
      isOptionsRevealEnabled,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      selectionIds,
      shouldRenderLoader,
      variantsAvailability,
      product,
      isVisible,
      readOnly,
      registerOptionRef,
    ]
  );

  const getTextualOptions = useCallback(() => {
    const textualOptions = getAllVisibleTextualOptions(product.options);
    return textualOptions.map((option) =>
      wrapAnimation(renderTextualOption(option), isVisible(option), isFirst(option))
    );
  }, [renderTextualOption, product.options, isVisible, isFirst, wrapAnimation]);

  const getFirstOption = useCallback(() => {
    const firstOption = product.options[0];
    if (!firstOption) {
      return null;
    }
    const optionType = firstOption.optionType;

    if (optionType === ProductOptionType.COLOR) {
      return renderColorOption(firstOption);
    } else {
      return renderTextualOption(firstOption);
    }
  }, [renderColorOption, renderTextualOption, product.options]);

  return shouldShowFirstProductOptionInGallery ? (
    <>
      <ConditionalRender by="showFirstOptionAnyType">{getFirstOption()}</ConditionalRender>
      <ConditionalRender by="showColorOption">{getColorOptions()}</ConditionalRender>
      <ConditionalRender by="showTextualOptions">{getTextualOptions()}</ConditionalRender>
    </>
  ) : (
    <>
      {getColorOptions()}
      <ConditionalRender by="showTextualOptionsWhenShowFirstOptionAnyTypeExperimentIsDisabled">
        {getTextualOptions()}
      </ConditionalRender>
    </>
  );
};

export const ProductOptions = withGlobals(ProductOptionsComponent);
